/* The Studio welcome — `V3-Tel-Powitanie` (phone) and `V3-Desk-Powitanie` (desktop), transcribed.
 *
 * The layout switches on the CONTAINER's width, never the viewport's: the builder draws this page in
 * a scaled phone frame inside a desktop window, and it must draw the phone composition there. The
 * phone composition is the default; the desktop one is the `studio-landing` container query.
 *
 * Every value is a Studio token (`tokens/studio.css`) or a theme variable the root carries
 * (`--studio-accent-fill` and friends, from `themeVarsFor`). The two name sizes arrive inline,
 * computed from the name and the face (`model/landing.ts`). */

.landing {
  container: studio-landing / inline-size;
  position: relative;

  /* Its own stacking context: the photo, the object, the name and the column order among themselves
   * and never against the builder's frame around a preview. */
  isolation: isolate;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  font-family: var(--studio-font-body);
  color: var(--studio-ink);
  background: var(--studio-canvas);
  -webkit-font-smoothing: antialiased;
}

/* The top padding is the room the photograph keeps above the name: on a short phone the lines under
 * the name would otherwise lift it over the face and the falling object out of the top edge. Past it
 * the stage grows and the page scrolls (fit or scroll, never clip). */
.stage {
  position: relative;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: var(--studio-landing-min-h);
  padding: var(--studio-name-floor) var(--space-4) var(--space-0);
}

/* ── The photograph, or the disc that stands in for one ──────────────────────────────────── */

.photo {
  position: absolute;
  inset: 0 0 auto;
  height: var(--studio-photo-h);
  overflow: hidden;
  pointer-events: none;
}

.photoDimmed {
  height: var(--studio-photo-dim-h);
}

.photoImage {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: var(--studio-photo-focus);
  filter: var(--photo-filter);
  animation-name: var(--studio-enter-portrait);
  animation-duration: var(--studio-dur-portrait);
  animation-timing-function: var(--ease);
  animation-fill-mode: both;
}

.photoDimmed .photoImage {
  filter: var(--studio-photo-filter-dim);
}

.photoFade {
  position: absolute;
  inset: 0;
  background: var(--studio-fade-cutout);
}

.photoDimmed .photoFade {
  background: var(--studio-fade-dimmed);
}

.disc {
  position: absolute;
  top: var(--studio-disc-top);
  left: 50%;
  width: var(--studio-disc-size);
  aspect-ratio: 1;
  border-radius: var(--r-circle);
  background: var(--studio-accent-fill);
  transform: translateX(-50%);
}

/* ── Header: logo, name, badge ──────────────────────────────────────────────────────────── */

.header {
  position: absolute;
  inset: var(--studio-header-top) var(--space-20px) auto;
  z-index: 4;
  display: flex;
  gap: var(--space-2);
  align-items: center;
  justify-content: space-between;
}

/* The brand name keeps its room — it shrinks only when it alone is wider than the header — and the
 * badge gives way instead, ending in an ellipsis. */
.headerBrand {
  flex: none;
  max-width: 100%;
}

.badge {
  flex: 0 1 auto;
  min-width: 0;
  padding: var(--space-6px) var(--space-10px);
  overflow: hidden;
  font-size: calc(var(--studio-fs-badge) * var(--studio-body-scale));
  font-weight: var(--fw-semibold);
  text-overflow: ellipsis;
  white-space: nowrap;
  border: var(--border-hairline) solid var(--studio-line-strong);
  border-radius: var(--r-pill);
}

/* ── The name, and the object that falls beside it ─────────────────────────────────────── */

.objectAnchor {
  position: relative;
  height: 0;
  margin-top: auto;
}

/* A stacking context of its own at the name's level, whatever animates inside or on it: a letter's
 * blur or slide, and the desktop's parallax transform, each made a context mid-entrance otherwise,
 * and the name changed places with the photograph and the object while it played. */
.nameBlock {
  position: relative;
  z-index: 3;
}

.name {
  position: relative;
  z-index: 3;
  margin: var(--space-0);
  font-family: var(--studio-font-head);
  font-size: var(--studio-name-size);
  font-weight: var(--studio-head-weight);
  font-optical-sizing: auto;
  line-height: var(--studio-head-leading);
  letter-spacing: var(--studio-head-track);
  text-transform: uppercase;
}

/* A heading the page puts the focus on, never one a person tabs to: no ring. */
.name:focus {
  outline: none;
}

.nameLine {
  display: block;
  padding-top: var(--studio-name-overhang);
  padding-bottom: var(--studio-name-descent);
  margin-top: var(--studio-name-overhang-back);
  margin-bottom: var(--studio-name-descent-back);
  overflow: hidden;
  white-space: nowrap;
}

.space {
  display: inline-block;
  width: var(--studio-name-word-gap);
}

.letter {
  display: inline-block;
  animation-name: var(--studio-enter-letter);
  animation-duration: var(--studio-dur-letter);
  animation-timing-function: var(--ease);
  animation-delay: calc(var(--studio-delay-letter) + var(--studio-letter-index) * var(--studio-step-letter));
  animation-fill-mode: both;
}

.object {
  position: absolute;
  top: var(--studio-object-offset);
  right: var(--studio-object-inset);
  z-index: 2;
  width: var(--studio-object-size);
  aspect-ratio: 1;
  pointer-events: none;
}

.objectDrop {
  width: 100%;
  height: 100%;
  animation-name: var(--studio-enter-drop);
  animation-duration: var(--studio-dur-drop);
  animation-timing-function: var(--ease);
  animation-delay: var(--studio-delay-drop);
  animation-fill-mode: both;
}

.objectImage {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  animation-name: var(--studio-ambient-float);
  animation-duration: var(--studio-dur-float);
  animation-timing-function: var(--studio-ease-float);
  animation-delay: var(--studio-delay-float);
  animation-iteration-count: infinite;
  animation-direction: alternate;
}

.objectLibrary .objectImage {
  filter: var(--studio-object-filter);
}

/* ── The strip under the name ───────────────────────────────────────────────────────────── */

.stripLine {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-height: var(--studio-strip-line-h);
  margin: var(--space-20px) var(--space-2px) var(--space-0);
  font-size: calc(var(--studio-fs-strip) * var(--studio-body-scale));
  font-weight: var(--fw-semibold);
  line-height: var(--studio-lh-strip);
  color: var(--studio-ink-muted);
  animation-name: var(--studio-enter-fade);
  animation-duration: var(--studio-dur-fade);
  animation-timing-function: var(--studio-ease-fade);
  animation-delay: var(--studio-delay-strip);
  animation-fill-mode: both;
}

/* Each line as typed: its own row, repeated spaces kept, a long one wrapped rather than cut. */
.stripRow {
  display: block;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

/* Where the trainer works: a pin and a quiet line under the name and the strip. Above the
 * photograph, as the name is: on a short screen the lines under the name climb onto it. */
.location {
  position: relative;
  z-index: 3;
  display: flex;
  gap: var(--space-6px);
  align-items: center;
  margin: var(--space-2) var(--space-2px) var(--space-0);
  font-size: calc(var(--studio-fs-strip) * var(--studio-body-scale));
  font-weight: var(--fw-semibold);
  color: var(--studio-ink-quiet);
  animation-name: var(--studio-enter-fade);
  animation-duration: var(--studio-dur-fade);
  animation-timing-function: var(--studio-ease-fade);
  animation-delay: var(--studio-delay-strip);
  animation-fill-mode: both;
}

.locationIcon {
  flex: none;
  width: var(--studio-location-icon);
  height: var(--studio-location-icon);
}

.locationText {
  min-width: 0;
  overflow-wrap: anywhere;
}

/* The slogans: the desktop's stacked kicker, set small and still under the name and the location
 * (owner, 28.09.2026 — the scrolling band it replaces was too loud for a phone). */
.kick {
  position: relative;
  z-index: 3;
  margin: var(--space-10px) var(--space-2px) var(--space-0);
  font-family: var(--studio-font-head);
  font-size: calc(var(--studio-fs-kick-phone) * var(--studio-head-scale));
  font-weight: var(--studio-head-weight);
  line-height: var(--studio-lh-kick);
  letter-spacing: var(--studio-head-track);
  overflow-wrap: anywhere;
  list-style: none;
  animation-name: var(--studio-enter-fade);
  animation-duration: var(--studio-dur-fade);
  animation-timing-function: var(--studio-ease-fade);
  animation-delay: var(--studio-delay-strip);
  animation-fill-mode: both;
}

/* ── The tiles ──────────────────────────────────────────────────────────────────────── */

.tiles {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-10px);
  height: var(--studio-tiles-h);
  margin-top: var(--space-10px);
}

.surveyTile {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: var(--space-14px) var(--space-4) var(--space-3);
  overflow: hidden;
  color: var(--studio-on-accent);
  background: var(--studio-accent-fill);
  border-radius: var(--studio-r-tile);
  animation-name: var(--studio-enter-rise);
  animation-duration: var(--studio-dur-rise);
  animation-timing-function: var(--ease-spring);
  animation-delay: var(--studio-delay-tile);
  animation-fill-mode: both;
}

.surveyLabel {
  font-size: calc(var(--studio-fs-tile-label) * var(--studio-body-scale));
  font-weight: var(--fw-bold);
}

.surveyCount {
  display: flex;
  gap: var(--space-6px);
  align-items: baseline;
  font-family: var(--studio-font-head);
}

.surveyFigure {
  font-size: calc(var(--studio-fs-tile-figure) * var(--studio-head-scale));
  font-weight: var(--studio-head-weight);
  font-variant-numeric: tabular-nums;
  line-height: var(--studio-lh-figure);
  letter-spacing: var(--studio-ls-figure);
}

.surveyWord {
  font-size: calc(var(--studio-fs-tile-word) * var(--studio-head-scale));
  font-weight: var(--studio-head-weight);
  letter-spacing: var(--studio-ls-tight);
}

.surveyTime {
  display: inline-flex;
  gap: var(--space-5px);
  align-items: center;
  align-self: flex-start;
  padding: var(--space-3px) var(--space-2) var(--space-3px) var(--space-6px);
  font-size: calc(var(--studio-fs-tile-label) * var(--studio-body-scale));
  font-weight: var(--fw-bold);
  font-variant-numeric: tabular-nums;

  /* An outline in the tile's own ink, never a tinted pill (owner, 28 September 2026). */
  border: var(--border-thick) solid var(--studio-on-accent);
  border-radius: var(--r-pill);
}

.surveyTimeIcon {
  flex: none;
  width: var(--studio-icon-xs);
  height: var(--studio-icon-xs);
}

.messageTile {
  display: flex;
  flex-direction: column;
  gap: var(--space-6px);
  padding: var(--space-3) var(--space-14px);
  overflow: hidden;
  background: var(--studio-surface-tile);
  border: var(--border-hairline) solid var(--studio-line-tile);
  border-radius: var(--studio-r-tile);
  animation-name: var(--studio-enter-rise);
  animation-duration: var(--studio-dur-rise);
  animation-timing-function: var(--ease-spring);
  animation-delay: var(--studio-delay-tile-2);
  animation-fill-mode: both;
}

.signatureRow {
  display: flex;
  gap: var(--space-6px);
  align-items: center;
  min-width: 0;
}

.messageTile .tileAvatar {
  width: var(--studio-avatar-sm);
  height: var(--studio-avatar-sm);
}

.signature {
  overflow: hidden;
  font-size: calc(var(--studio-fs-signature) * var(--studio-body-scale));
  font-weight: var(--fw-bold);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.message {
  display: -webkit-box;
  margin: var(--space-0);
  overflow: hidden;
  font-size: calc(var(--studio-fs-signature) * var(--studio-body-scale));
  line-height: var(--studio-lh-message);
  color: var(--studio-ink-soft);
  overflow-wrap: anywhere;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
}

/* Desktop-only parts of the column; the phone draws the message inside its second tile. */
.messageCard,
.hint,
.bar {
  display: none;
}

/* ── The call to action ─────────────────────────────────────────────────────────────────── */

.cta {
  margin-top: var(--space-2);
  animation-name: var(--studio-enter-rise);
  animation-duration: var(--studio-dur-rise);
  animation-timing-function: var(--ease-spring);
  animation-delay: var(--studio-delay-cta);
  animation-fill-mode: both;
}

.footer {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  align-items: center;
  margin: var(--space-14px) var(--space-0) var(--space-3);
  animation-name: var(--studio-enter-fade);
  animation-duration: var(--studio-dur-fade);
  animation-timing-function: var(--studio-ease-fade);
  animation-delay: var(--studio-delay-foot);
  animation-fill-mode: both;
}

/* On a phone the column's children are the stage's own flex items; a desktop positions it. */
.column {
  display: contents;
}

/* ── Desktop ────────────────────────────────────────────────────────────────────────────── */

@container studio-landing (min-width: 1024px) {
  .stage {
    display: block;
    min-height: var(--studio-landing-desk-min-h);
    padding: var(--space-0);
  }

  .parallaxPortrait {
    transform: translate3d(
      calc(var(--studio-px, 0) * -6px),
      calc(var(--studio-py, 0) * -4.2px),
      0
    );
    transition-property: transform;
    transition-duration: var(--studio-dur-parallax);
    transition-timing-function: var(--ease);
  }

  .parallaxName {
    transform: translate3d(calc(var(--studio-px, 0) * 12px), calc(var(--studio-py, 0) * 8.4px), 0);
    transition-property: transform;
    transition-duration: var(--studio-dur-parallax);
    transition-timing-function: var(--ease);
  }

  .parallaxObject {
    transform: translate3d(calc(var(--studio-px, 0) * 24px), calc(var(--studio-py, 0) * 16.8px), 0);
    transition-property: transform;
    transition-duration: var(--studio-dur-parallax);
    transition-timing-function: var(--ease);
  }

  .photo,
  .photoDimmed {
    left: var(--studio-desk-photo-left);
    width: var(--studio-desk-photo-w);
    height: var(--studio-desk-photo-h);
  }

  .photoFade,
  .photoDimmed .photoFade {
    background: var(--studio-fade-desk);
  }

  .photoImage {
    object-position: var(--studio-photo-focus-desk);
  }

  .disc {
    top: var(--studio-desk-disc-top);
    left: var(--studio-desk-disc-left);
    width: var(--studio-desk-disc-size);
    transform: none;
  }

  .header {
    inset: var(--space-8) var(--space-12) auto;
    z-index: 6;
  }

  .badge {
    padding: var(--space-2) var(--space-14px);
    font-size: calc(var(--studio-fs-badge-desk) * var(--studio-body-scale));
  }

  .kick {
    position: absolute;
    top: var(--studio-desk-kick-top);
    left: var(--space-12);
    z-index: 4;
    width: var(--studio-desk-kick-w);
    margin: var(--space-0);
    font-size: calc(var(--studio-fs-kick) * var(--studio-head-scale));

    /* The phone breaks a word too long for its line; the desktop's column never did. */
    overflow-wrap: normal;
    animation-name: var(--studio-enter-slide);
    animation-duration: var(--studio-dur-kick);
    animation-timing-function: var(--ease);
    animation-delay: var(--studio-delay-kick);
    animation-fill-mode: both;
  }

  .nameBlock {
    position: absolute;
    bottom: var(--studio-desk-name-bottom);
    left: var(--studio-desk-name-left);
    margin: var(--space-0);
  }

  .name {
    font-size: var(--studio-name-size-desk);
  }

  .nameLine {
    display: inline-block;
  }

  .nameLine + .nameLine {
    margin-left: var(--studio-name-word-gap);
  }

  .objectAnchor {
    position: static;
  }

  .object {
    position: absolute;
    top: auto;
    right: var(--studio-desk-object-right);
    bottom: var(--studio-desk-object-bottom);
    z-index: 4;
    width: var(--studio-desk-object-size);
  }

  .stripLine,
  .location,
  .footer {
    display: none;
  }

  .column {
    position: absolute;
    top: var(--studio-desk-column-top);
    right: var(--space-12);
    z-index: 5;
    display: flex;
    flex-direction: column;
    gap: var(--space-14px);
    width: var(--studio-desk-column-w);
  }

  .messageCard {
    display: block;
    padding: var(--space-20px) var(--space-22px);
    background: var(--studio-surface-card);
    border: var(--border-hairline) solid var(--studio-line-card);
    border-radius: var(--studio-r-tile-lg);
    animation-name: var(--studio-enter-rise);
    animation-duration: var(--studio-dur-rise);
    animation-timing-function: var(--ease-spring);
    animation-delay: var(--studio-delay-card);
    animation-fill-mode: both;
  }

  .messageCard .cardAvatar {
    width: var(--studio-avatar-md);
    height: var(--studio-avatar-md);
  }

  .cardHead {
    display: flex;
    gap: var(--space-10px);
    align-items: center;
    min-width: 0;
  }

  .cardWho {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .cardName {
    overflow: hidden;
    font-size: calc(var(--studio-fs-card-name) * var(--studio-body-scale));
    font-weight: var(--fw-bold);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .cardSub {
    font-size: calc(var(--studio-fs-signature) * var(--studio-body-scale));
    color: var(--studio-ink-faint);
  }

  /* Clamped so a long message cannot push the column down into the name. */
  .cardMessage {
    display: -webkit-box;
    margin: var(--space-14px) var(--space-0) var(--space-0);
    overflow: hidden;
    font-size: calc(var(--studio-fs-card-message) * var(--studio-body-scale));
    line-height: var(--studio-lh-card-message);
    color: var(--studio-ink-strong);
    overflow-wrap: anywhere;
    -webkit-line-clamp: 6;
    -webkit-box-orient: vertical;
  }

  /* One tile on a desktop: the survey takes the row (the „only the trainer sees" tile beside it
   * was dropped by the owner on 28.09.2026 as saying what the page already says). */
  .tiles {
    grid-template-columns: minmax(0, 1fr);
    gap: var(--space-3);
    height: var(--studio-tiles-h-desk);
    margin: var(--space-0);
  }

  .surveyTile {
    padding: var(--space-4) var(--space-5) var(--space-14px);
    border-radius: var(--studio-r-tile-lg);
  }

  .surveyLabel {
    font-size: calc(var(--studio-fs-tile-label-desk) * var(--studio-body-scale));
  }

  .surveyCount {
    gap: var(--space-7px);
  }

  .surveyFigure {
    font-size: calc(var(--studio-fs-tile-figure-desk) * var(--studio-head-scale));
    line-height: var(--studio-lh-figure-desk);
  }

  .surveyWord {
    font-size: calc(var(--studio-fs-tile-word-desk) * var(--studio-head-scale));
  }

  .surveyTime {
    padding: var(--space-3px) var(--space-9px) var(--space-3px) var(--space-7px);
    font-size: calc(var(--studio-fs-signature) * var(--studio-body-scale));
  }

  .messageTile {
    display: none;
  }

  .cta {
    margin: var(--space-0);
  }

  .hint {
    display: flex;
    gap: var(--space-2);
    align-items: center;
    justify-content: center;
    font-size: calc(var(--studio-fs-hint) * var(--studio-body-scale));
    color: var(--studio-ink-faint);
    animation-name: var(--studio-enter-fade);
    animation-duration: var(--studio-dur-fade);
    animation-timing-function: var(--studio-ease-fade);
    animation-delay: var(--studio-delay-hint);
    animation-fill-mode: both;
  }

  .bar {
    position: absolute;
    inset: auto var(--space-12) 0;
    z-index: 6;
    display: flex;
    gap: var(--space-4);
    align-items: center;
    justify-content: space-between;
    min-height: var(--studio-desk-bar-h);
    padding: var(--space-3) var(--space-0);
    animation-name: var(--studio-enter-fade);
    animation-duration: var(--studio-dur-fade);
    animation-timing-function: var(--studio-ease-fade);
    animation-delay: var(--studio-delay-strip);
    animation-fill-mode: both;
  }

  .barStart {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: var(--space-1);
    min-width: 0;
  }

  .barLine {
    margin: var(--space-0);
    font-size: calc(var(--studio-fs-bar) * var(--studio-body-scale));
    font-weight: var(--fw-semibold);
    line-height: var(--studio-lh-strip);
    color: var(--studio-ink-muted);
  }

  .barLocation {
    display: flex;
    gap: var(--space-6px);
    align-items: center;
    margin: var(--space-0);
    font-size: calc(var(--studio-fs-strip) * var(--studio-body-scale));
    font-weight: var(--fw-semibold);
    color: var(--studio-ink-quiet);
  }

  .barLinks {
    flex: none;
  }

  .barMark {
    flex: none;
  }
}

